/**
 * 我的订单（买卖视角切换 + 状态 Tab + 待处理置顶 US-RET-05）
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listOrders } from '@/api/trade';
import { useAuth } from '@/stores/auth';
import type { TradeOrder } from '@/types';
import { cosUrl, imgFallback, isOrderSettled, orderStatusMeta } from '@/utils/constants';
import { formatDateTime, formatMoney, maskName } from '@/utils/format';
import StatusTag from '@/components/common/StatusTag';
import Countdown from '@/components/common/Countdown';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/order.css';

// 状态 Tab 对齐后端 model/trade.go 代码枚举（P0-5 2026-08-20）：
// 0-待付款 1-已付款 2-已发货 3-已完成 4-已取消 5-已退款 6-已过期 7-售后中；
// 「已结算」为 Completed(3)+settled_at 派生态（客户端过滤，后端无独立查询参数）。
const TABS: { key: string; label: string; match: (o: TradeOrder) => boolean }[] = [
  { key: 'all', label: '全部', match: () => true },
  { key: '0', label: '待付款', match: (o) => o.order_status === 0 },
  { key: '1', label: '已付款', match: (o) => o.order_status === 1 },
  { key: '2', label: '已发货', match: (o) => o.order_status === 2 },
  { key: '3', label: '已完成', match: (o) => o.order_status === 3 },
  { key: 'settled', label: '已结算', match: (o) => isOrderSettled(o) },
  { key: '4', label: '已取消', match: (o) => o.order_status === 4 },
  { key: '5', label: '已退款', match: (o) => o.order_status === 5 },
  { key: '6', label: '已过期', match: (o) => o.order_status === 6 },
  { key: '7', label: '售后中', match: (o) => o.order_status === 7 },
];

export default function OrderList() {
  const { user } = useAuth();
  const [view, setView] = useState<'buy' | 'sell'>('buy');
  const [tab, setTab] = useState('all');
  const [orders, setOrders] = useState<TradeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listOrders(view, user?.id ?? 0);
      setOrders(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [view, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const activeTab = TABS.find((t) => t.key === tab);
    let list = activeTab ? orders.filter(activeTab.match) : orders;
    // 待处理置顶：待付款(0) > 已付款(1) > 已发货(2) > 其他
    const rank = (o: TradeOrder): number => {
      if (o.order_status === 0) return 0;
      if (o.order_status === 1) return 1;
      if (o.order_status === 2) return 2;
      return 3;
    };
    list = [...list].sort((a, b) => rank(a) - rank(b) || new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return list;
  }, [orders, tab]);

  const counterparty = (o: TradeOrder): string => {
    if (view === 'buy') {
      return o.seller?.enterprise_name || o.seller?.real_name ? maskName(o.seller?.real_name) : '卖家';
    }
    return o.buyer?.real_name ? maskName(o.buyer?.real_name) : '买家';
  };

  return (
    <div className="order-page">
      <PageHero eyebrow="MY ORDERS" title="我的订单" desc="跟踪订单全流程：付款 / 交割 / 合同 / 存证 / 评价" />

      <div className="container">
        <div className="order-view">
          <button className={`order-view__btn ${view === 'buy' ? 'active' : ''}`} onClick={() => setView('buy')}>
            我买到的
          </button>
          <button className={`order-view__btn ${view === 'sell' ? 'active' : ''}`} onClick={() => setView('sell')}>
            我卖出的
          </button>
        </div>

        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Loading rows={5} type="list" />
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : filtered.length === 0 ? (
          <Empty icon="📦" title="暂无订单" desc="去市场挑选心仪的牛只吧" action={<Link className="btn btn-primary btn-sm" to="/market">去逛逛</Link>} />
        ) : (
          <div className="order-list">
            {filtered.map((o) => {
              const item = o.items?.[0];
              const cattle = item?.cattle;
              const settled = isOrderSettled(o);
              const statusMeta = orderStatusMeta(o.order_status, settled);
              return (
                <Link className="order-item" to={`/orders/${o.id}`} key={o.id}>
                  <div className="order-item__thumb">
                    <img
                      src={cattle?.images?.[0] ?? cattle?.face_image_url ?? cosUrl('/images/cattle-default.jpg')}
                      alt=""
                      data-local="/images/cattle-default.jpg"
                      onError={imgFallback}
                    />
                  </div>
                  <div className="order-item__main">
                    <div className="order-item__title">
                      <span className="order-item__no">{o.order_no}</span>
                      <StatusTag type="custom" label={statusMeta.label} tone={statusMeta.tone} />
                    </div>
                    <div className="order-item__desc">
                      {cattle?.breed} · {cattle?.ear_tag_no} · {item?.quantity} 头
                      <span className="order-item__time">{formatDateTime(o.created_at)}</span>
                    </div>
                    <div className="order-item__meta">
                      交易对方：{counterparty(o)} · {view === 'buy' ? '买入' : '卖出'}
                      {o.order_status === 0 && o.expire_at ? (
                        <span className="order-item__countdown">
                          <Countdown endTime={o.expire_at} prefix="剩余" expiredText="已超时" />
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="order-item__amount">
                    <b>{formatMoney(o.paid_amount || o.total_amount)}</b>
                    <span>{statusMeta.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
